import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import QueryError from './components/QueryError';
import DarkModeToggle from './components/DarkModeToggle';
import UserMenu from './components/UserMenu';
import AdminLinks from './components/AdminLinks';
import MobileMenu from './components/MobileMenu';
import PageTransition from './components/PageTransition';
import { SnackbarProvider } from './components/M3Snackbar';
import M3NavigationDrawer, { NavDrawerItem } from './components/M3NavigationDrawer';
import { useMutation } from '@apollo/client/react';
import { useAuth } from './AuthContext';
import { apolloClient } from './client';
import { LOGOUT } from './graphql/auth';
import './Layout.css';

export const NAV_ITEMS = [
  { path: '/graves', label: 'Graves' },
  { path: '/lithics', label: 'Lithics' },
  { path: '/ceramics', label: 'Ceramics' },
  { path: '/analytics', label: 'Analytics' },
  { path: '/publications', label: 'Publications' },
  { path: '/sites', label: 'Sites' },
  { path: '/tags', label: 'Tags' },
  { path: '/teams', label: 'Teams' }
];

export default function Layout() {
  const { user, error } = useAuth();
  const navigate = useNavigate();
  const [logoutMutation] = useMutation(LOGOUT);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() =>
    JSON.parse(localStorage.getItem('darkMode') || 'false')
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for flash message on mount and path changes
  useEffect(() => {
    const el = document.querySelector('[data-flash]');
    if (el) {
      const msg = el.getAttribute('data-flash');
      if (msg) {
        setFlash(msg);
        el.removeAttribute('data-flash');
      }
    }
  }, [location.pathname]);

  if (error)
    return (
      <div className="p-3">
        <QueryError error={error} />
      </div>
    );

  const isAdmin = user?.role === 'admin';

  // Drawer navigation items
  const drawerItems = useMemo(
    () => [
      { icon: 'grave', label: 'Graves', path: '/graves' },
      { icon: 'inventory_2', label: 'Lithics', path: '/lithics' },
      { icon: 'profile', label: 'Ceramics', path: '/ceramics' },
      { icon: 'insights', label: 'Analytics', path: '/analytics' },
      { icon: 'menu_book', label: 'Publications', path: '/publications' },
      { icon: 'public', label: 'Sites', path: '/sites' },
      { icon: 'label', label: 'Tags', path: '/tags' },
      { icon: 'groups', label: 'Teams', path: '/teams' }
    ],
    []
  );

  const handleLogout = async () => {
    await logoutMutation();
    await apolloClient.clearStore();
    navigate('/login');
  };

  const handleNavClick = (path) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const dismissFlash = () => setFlash(null);

  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {/* Navigation Drawer (desktop persistent, mobile dismissible) */}
      <M3NavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="dismissible"
        header={
          <>
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 28, color: 'var(--md-sys-color-primary)' }}
            >
              science
            </span>
            <span style={{ fontSize: 18, fontWeight: 600 }}>AutArch</span>
          </>
        }
        content={
          <>
            {drawerItems.map((item) => (
              <NavDrawerItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                active={location.pathname === item.path}
                onClick={() => {
                  handleNavClick(item.path);
                  setDrawerOpen(false);
                }}
              />
            ))}
          </>
        }
      />

      {/* Top Navbar */}
      <nav
        className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="navbar-content">
          <div
            className="navbar-drawer-toggle"
            onClick={() => setDrawerOpen(!drawerOpen)}
            aria-label="Toggle navigation menu"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setDrawerOpen(!drawerOpen)}
          >
            <span className="material-symbols-rounded">menu</span>
          </div>
          <Link to="/" className="navbar-brand" aria-label="AutArch home">
            <span className="material-symbols-rounded">science</span>
            AutArch
          </Link>
        </div>

        {/* Main Navigation */}
        <div className="navbar-nav">
          <div className="nav-items">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNavClick(item.path)}
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {isAdmin && <AdminLinks />}

          {/* Right-aligned actions */}
          <div className="navbar-actions">
            <DarkModeToggle darkMode={darkMode} onToggle={() => setDarkMode(!darkMode)} />

            {user && <UserMenu user={user} onLogout={handleLogout} />}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <span className="material-symbols-rounded">{mobileMenuOpen ? 'close' : 'menu'}</span>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      <MobileMenu mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      {/* Main Content */}
      <SnackbarProvider>
        <div className="app-body">
          <main id="main-content" className="app-main">
            <div className="content-wrapper">
              {flash && (
                <div className="flash-message" role="alert">
                  <span className="material-symbols-rounded">info</span>
                  <span>{flash}</span>
                  <button
                    className="flash-dismiss"
                    onClick={dismissFlash}
                    aria-label="Dismiss message"
                  >
                    <span className="material-symbols-rounded">close</span>
                  </button>
                </div>
              )}
              <PageTransition>
                <Outlet />
              </PageTransition>
            </div>
          </main>
        </div>
      </SnackbarProvider>
    </div>
  );
}
